/**
 * Constants for the ResourcesRevised module
 * This file centralizes all constants used across the ResourcesRevised components
 */

// Content Types
export const CONTENT_TYPES = {
	ARTICLES  : 'Articles' as const,
	VIDEOS    : 'Videos' as const,
	PODCASTS  : 'Podcasts' as const,
	DOCUMENTS : 'Documents' as const,
	IMAGES    : 'Images' as const,
	LINKS     : 'Links' as const,
	PLAYLISTS : 'Playlists' as const,
	AUDIOS    : 'Audios' as const,
	ALL       : 'All' as const,
	DRAFTS    : 'Drafts' as const
};

// Content Section Types
export const CONTENT_SECTIONS = {
	TRENDING   : 'trending',
	CONTINUE   : 'continue',
	LATEST     : 'latest',
	ARTICLES   : 'articles',
	VIDEOS     : 'videos',
	PODCASTS   : 'podcasts',
	DOCUMENTS  : 'documents',
	IMAGES     : 'images',
	PLAYLISTS  : 'playlists',
	CATEGORIES : 'categories'
};

// Content Status Types
export const CONTENT_STATUS = {
	DRAFT       : 'Draft' as const,
	PUBLISHED   : 'Published' as const,
	ARCHIVED    : 'Archived' as const,
	SCHEDULED   : 'Scheduled' as const,
	UNPUBLISHED : 'Unpublished' as const
};

// View Types
export const VIEW_TYPES = {
	GRID  : 'grid' as const,
	LIST  : 'list' as const,
	CHART : 'chart' as const
};

// Resource Types
export const RESOURCE_TYPES = {
	MY_RESOURCES : 'myres' as const,
	NETWORKS     : 'networks' as const
};

// Sort Options
export const SORT_OPTIONS = {
	NEWEST       : 'newest' as const,
	OLDEST       : 'oldest' as const,
	POPULAR      : 'popular' as const,
	ALPHABETICAL : 'alphabetical' as const
};

// View State Types
export const VIEW_STATE_TYPES = {
	CATEGORIES     : 0,
	SUBCATEGORIES  : 1,
	CONTENT_DETAIL : 2
};

// Route Patterns
export const ROUTES = {
	RESOURCE               : '/resource',
	RESOURCE_NETWORKS      : '/resource/mynetworks',
	RESOURCE_FAVORITES     : '/resource/favorites',
	RESOURCE_SUBCATEGORIES : '/resource/subCategories',
	RESOURCE_SUBCATEGORY   : '/resource/subCategories/:id',
	RESOURCE_COMPANY       : '/resource/:companyId',
	RESOURCE_PLAYLIST      : '/resource/playlist',
	BUSINESS_SETTINGS      : '/businessSettings/content'
};

// Media Providers
export const MEDIA_PROVIDERS = {
	YOUTUBE  : 'YouTube' as const,
	VIMEO    : 'Vimeo' as const,
	INTERNAL : 'Internal' as const
};

// UI Constants
export const UI = {
	COLORS : {
		WHITE          : '#FFFFFF',
		BLACK          : '#000000',
		PRIMARY        : 'var(--primaryColor)',
		SECONDARY      : 'var(--secondaryColor)',
		TEXT_PRIMARY   : 'var(--textPrimary)',
		TEXT_SECONDARY : 'var(--textSecondary)',
		BACKGROUND     : 'var(--backgroundColor)',
		BORDER         : 'var(--borderColor)',
		DISABLED       : 'var(--disabledColor)',
		ERROR          : 'var(--errorColor)'
	},
	SPACING : {
		PIXEL_0     : '0px',
		PIXEL_1     : '1px',
		PIXEL_2     : '2px',
		PIXEL_4     : '4px',
		PIXEL_6     : '6px',
		PIXEL_8     : '8px',
		PIXEL_10    : '10px',
		PIXEL_12    : '12px',
		PIXEL_20    : '20px',
		PIXEL_16    : '16px',
		PIXEL_18    : '18px',
		PIXEL_22    : '22px',
		PIXEL_28    : '28px',
		PIXEL_32    : '32px',
		PIXEL_34    : '34px',
		PIXEL_40    : '40px',
		PIXEL_64    : '64px',
		PIXEL_300   : '300px',
		PERCENT_60  : '60%',
		PERCENT_100 : '100%'
	},
	FONT_SIZES : {
		PIXEL_10 : '10px',
		PIXEL_12 : '12px',
		PIXEL_14 : '14px',
		PIXEL_16 : '16px',
		PIXEL_18 : '18px',
		PIXEL_20 : '20px',
		PIXEL_24 : '24px',
		REM_0_8  : '0.8rem',
		REM_1_5  : '1.5rem',
		REM_2_5  : '2.5rem'
	},
	FONT_WEIGHTS : {
		FONT_400 : 400,
		FONT_500 : 500,
		FONT_600 : 600,
		FONT_700 : 700
	},

	// Border Radius
	BORDER_RADIUS : {
		DEFAULT : 'var(--pixel4)',
		SMALL   : '4px',
		MEDIUM  : '6px',
		LARGE   : '8px',
		CIRCLE  : '50%'
	},

	// Z-Index
	Z_INDEX : {
		HEADER    : 99,
		ARROW_NAV : 9
	},

	// Shadows
	SHADOWS : {
		SMALL  : '0px 1px 2px rgba(0,0,0,0.1)',
		MEDIUM : '0px 4px 6px rgba(0,0,0,0.1)',
		LARGE  : '0px 10px 20px rgba(0,0,0,0.1)'
	},

	// Animation
	TRANSITIONS : {
		DEFAULT   : 'opacity 0.3s ease-in-out, visibility 0.3s ease-in-out',
		TRANSFORM : 'transform 0.3s ease-in-out'
	},

	// Gradients
	GRADIENTS : {
		PRIMARY     : 'linear-gradient(180deg, #FFF 43.5%, #F1FFEA 100%)' as const,
		WHITE_GREEN : 'linear-gradient(180deg, #FFF 43.5%, #F1FFEA 100%)' as const,
		ORANGE      : 'linear-gradient(0deg, rgba(196, 138, 32, 0.1) 0%, rgba(255, 255, 255, 1) 100%)' as const,
		RED         : 'linear-gradient(0deg, rgba(180, 96, 90, 0.1) 0%, rgba(255, 255, 255, 1) 100%)' as const,
		BLUE        : 'linear-gradient(0deg, rgba(37, 110, 166, 0.1) 0%, rgba(255, 255, 255, 1) 100%)' as const,
		ORANGE_ALT  : 'linear-gradient(0deg, rgba(196, 138, 32, 0.1) 0%, rgba(255, 255, 255, 1) 100%)' as const,
		LIGHT_BLUE  : 'linear-gradient(0deg, rgba(5, 169, 224, 0.1) 0%, rgba(255, 255, 255, 1) 100%)' as const,
		PURPLE      : 'linear-gradient(0deg, rgba(248, 221, 255, 0.1) 0%, rgba(255, 255, 255, 1) 100%)' as const
	}
};

// Default values
export const DEFAULTS = {
	ITEMS_PER_VIEW         : 4,
	ITEMS_PER_VIEW_MOBILE  : 1,
	ITEMS_PER_VIEW_TABLET  : 2,
	ITEMS_PER_VIEW_DESKTOP : 4
};

// Breakpoints
export const BREAKPOINTS = {
	MOBILE : {
		TOP : '112px'
	},
	DESKTOP : {
		TOP : '124px'
	}
};

// Button variants
export const BUTTON_VARIANTS = {
	TEXT      : 'text' as const,
	OUTLINED  : 'outlined' as const,
	CONTAINED : 'contained' as const
};

// Button colors
export const BUTTON_COLORS = {
	PRIMARY   : 'primary' as const,
	SECONDARY : 'secondary' as const
};

// Button sizes
export const BUTTON_SIZES = {
	SMALL  : 'small' as const,
	MEDIUM : 'medium' as const,
	LARGE  : 'large' as const
};
